import { motion, AnimatePresence } from "framer-motion";
import { X, Instagram, Facebook, Phone, Split, ScrollText, AlertCircle, ImageIcon, ChevronLeft, ChevronRight } from "lucide-react";
import { useState } from "react";
import { formatDate } from "../../utils/Utils";
import moment from "moment";

const ViewDealModal = ({ viewModal, onCloseViewModal, deal }) => {
  const [activeTab, setActiveTab] = useState("details");
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showImageGallery, setShowImageGallery] = useState(false);

  const getStatusColor = (status, deal) => {
    // Check flash sale first
    if (deal?.validity_type === "flash_sale") {
      // If flash data exists and is valid
      if (deal?.flash && Array.isArray(deal.flash) && deal.flash.length > 0) {
        const isFlashExpired = deal.flash.some(flash => new Date(flash.flash_end_time) < new Date());
        const isFlashActive = deal.flash.some(flash => 
          new Date() >= new Date(flash.flash_start_time) && new Date() <= new Date(flash.flash_end_time)
        );
        
        if (isFlashExpired) return "bg-red-600";
        if (isFlashActive) return "bg-red-600 animate-pulse";
        return "bg-orange-600";
      } else {
        // Flash sale validity type but no flash data - treat as scheduled
        return "bg-orange-600";
      }
    }
    
    // Regular status handling
    switch (status.toLowerCase()) {
      case "Pending":
        return "bg-yellow-600";
      case "Approved":
        return "bg-green-600";
      case "Rejected":
        return "bg-red-600";
      case "Expired":
        return "bg-red-600";
default:
        return "bg-gray-700";
    }
  };

  // Helper functions for image gallery
  const getAllImages = () => {
    const images = [];
    if (deal?.image) {
      images.push(deal.image);
    }
    if (deal?.images && deal.images.length > 0) {
      images.push(...deal.images);
    }
    return images;
  };

  const nextImage = () => {
    const images = getAllImages();
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    const images = getAllImages();
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const openImageGallery = (index = 0) => {
    setCurrentImageIndex(index);
    setShowImageGallery(true);
  };

  return (
    <AnimatePresence>
      {viewModal && (
        <div className="fixed inset-0 flex items-center justify-center z-40">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/30"
            onClick={onCloseViewModal}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="relative w-[90%] max-w-6xl bg-white rounded-2xl shadow-xl z-50 flex flex-col max-h-[90vh]"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 md:p-6 border-b">
              <h2 className="text-xl md:text-2xl font-semibold text-gray-900">
                Deal Details
              </h2>
              <div className="flex items-center gap-2 md:gap-4">
                <div className="flex items-center gap-1 md:gap-2">
                  <span className="text-gray-600 text-sm md:text-base">
                    Status
                  </span>
                  <span
                    className={`px-3 py-1 text-sm font-medium text-white ${getStatusColor(
                      deal.status,
                      deal
                    )}  rounded-full`}
                  >
                    {(() => {
                      // Flash sale status
                      if (deal?.validity_type === "flash_sale") {
                        // If flash data exists and is valid
                        if (deal?.flash && Array.isArray(deal.flash) && deal.flash.length > 0) {
                          const isFlashExpired = deal.flash.some(flash => new Date(flash.flash_end_time) < new Date());
                          const isFlashActive = deal.flash.some(flash => 
                            new Date() >= new Date(flash.flash_start_time) && new Date() <= new Date(flash.flash_end_time)
                          );
                          
                          if (isFlashExpired) return "Flash Sale Expired";
                          if (isFlashActive) return "Flash Sale Active";
                          return "Flash Sale Scheduled";
                        } else {
                          // Flash sale validity type but no flash data
                          return "Flash Sale Scheduled";
                        }
                      }
                      
                      // Regular status
                      if (deal.status === "Approved") return "Approved";
                      if (deal.status === "Pending") return "Pending";
                      if (deal.status === "Expired") return "Expired";
                      return "Inactive";
                    })()}
                  </span>
                </div>
                <button
                  onClick={onCloseViewModal}
                  className="p-2 hover:bg-gray-100 rounded-xl transition-colors duration-200"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-4 md:p-6 overflow-y-auto flex-1">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                {/* Left Column - Image */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="flex flex-col overflow-hidden justify-start items-center p-4 bg-purple-100 rounded-2xl md:sticky md:top-0"
                >
                  <div className="relative w-full max-w-md">
                    <img
                      src={deal?.image || "https://example.com/placeholder.jpg"}
                      alt="Deal"
                      className="w-full h-auto max-h-[240px] rounded-xl cursor-pointer hover:opacity-90 transition-opacity object-cover"
                      onClick={() => openImageGallery(0)}
                    />
                    
                    {/* Image Gallery Indicator */}
                    {getAllImages().length > 1 && (
                      <div className="absolute top-2 right-2 bg-black/50 text-white px-2 py-1 rounded-full text-xs flex items-center justify-center gap-1">
                        <ImageIcon className="w-3 h-3" />
                        <span>{getAllImages().length}</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Additional Images Thumbnails */}
                  {deal?.images && deal.images.length > 0 && (
                    <div className="mt-2 w-full flex flex-col items-center">
                      <div className="flex gap-2 justify-center overflow-x-auto max-w-full pb-1">
                        {deal.images.slice(0, 5).map((image, index) => (
                          <img
                            key={index}
                            src={image}
                            alt={`Additional ${index + 1}`}
                            className="w-16 h-16 rounded-lg cursor-pointer hover:opacity-80 transition-opacity object-cover flex-shrink-0 border-2 border-gray-200 hover:border-purple-300"
                            onClick={() => openImageGallery(index + 1)}
                          />
                        ))}
                        {deal.images.length > 5 && (
                          <div 
                            className="w-16 h-16 rounded-lg bg-gray-200 flex items-center justify-center cursor-pointer hover:bg-gray-300 transition-colors flex-shrink-0 border-2 border-gray-200 hover:border-purple-300"
                            onClick={() => openImageGallery(0)}
                          >
                            <span className="text-xs font-medium text-gray-600">
                              +{deal.images.length - 5}
                            </span>
                          </div>
                        )}
                      </div>
                      {deal.images.length > 1 && (
                        <p className="text-xs text-gray-500 mt-1 text-center">
                          Click any image to view full gallery
                        </p>
                      )}
                    </div>
                  )}
                </motion.div>

                {/* Right Column - Details */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="flex flex-col space-y-4 md:space-y-6"
                >
                  <h3 className="text-lg font-bold text-gray-900 md:text-xl">
                    {deal?.title || "Loading..."}
                  </h3>

                  {/* Tabs Navigation */}
                  <div className="flex overflow-x-auto space-x-1 border-b scrollbar-hide">
                    {[
                      { id: "details", label: "Details" },
                      { id: "categories", label: "Categories" },
                      { id: "contact", label: "Contact" },
                      { id: "locations", label: "Locations" },
                      { id: "terms", label: "T&Cs" },
                    ].map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`px-3 md:px-4 py-2 text-xs md:text-[12px] font-medium whitespace-nowrap transition-colors duration-200
                          ${
                            activeTab === tab.id
                              ? "text-pink-600 border-b-2 border-pink-600"
                              : "text-gray-600 hover:text-pink-600"
                          }`}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  {/* Scrollable Tab Content */}
                  <div className="overflow-y-auto flex-1 md:max-h-[calc(95vh-320px)] pr-6 max-h-full">
                    <div className="pt-4 pr-2">
                    {activeTab === "details" && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="space-y-3"
                      >
                        {/* Price Information - Handle different offer types */}
                        {(() => {
                          // Helper function to check if BOGO has valid data
                          const hasBogoData = (bogo) => {
                            if (!bogo) return false;
                            // Handle both array and object formats
                            const bogoData = Array.isArray(bogo) ? bogo[0] : bogo;
                            return bogoData && (
                              bogoData.bogo_item_price || 
                              bogoData.item_price ||
                              (bogoData.bogo_quantity && bogoData.bogo_free_quantity) ||
                              (bogoData.quantity && bogoData.free_quantity)
                            );
                          };

                          // Helper function to check if Bundle has valid data
                          const hasBundleData = (bundle) => {
                            if (!bundle) return false;
                            // Handle both array and object formats
                            const bundleData = Array.isArray(bundle) ? bundle[0] : bundle;
                            return bundleData && (
                              bundleData.original_sum && 
                              bundleData.bundle_price && 
                              bundleData.bundle_savings
                            );
                          };

                          // Determine offer type based on data structure
                          const offerType = deal.offer_type || 
                            (hasBundleData(deal.bundle) ? 'bundle' : 
                             hasBogoData(deal.bogo) ? 'bogo' : 
                             (deal.discounted_price && deal.discounted_price !== 0) ? 'discount' : 'standard');

                          // Only show pricing for discount offers or when no special offer type
                          if (offerType === 'discount' || (offerType === 'standard' && deal.price && deal.price !== 0)) {
                            const originalPrice = deal.price;
                            const discountedPrice = deal.discounted_price || deal.discountedPrice;
                            const discountPercentage = deal.discount_percentage || deal.discountPercentage;
                            
                            // Calculate discount percentage if not provided
                            const calculatedDiscount = discountedPrice && originalPrice ? 
                              Math.round(((originalPrice - discountedPrice) / originalPrice) * 100) : discountPercentage;

                            return (
                              <div className="flex flex-col gap-2 sm:flex-row">
                                <div className="flex-1 p-3 bg-gray-50 rounded-lg">
                                  <div className="flex flex-col">
                                    <span className="text-xs text-gray-500">
                                      Original Price
                                    </span>
                                    <div className="flex items-baseline">
                                      <span className="text-xl font-bold text-gray-900">
                                        ₵{parseFloat(originalPrice).toFixed(2)}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                {discountedPrice && discountedPrice !== 0 && (
                                  <div className="flex-1 p-3 bg-gray-50 rounded-lg">
                                    <div className="flex flex-col">
                                      <span className="text-xs text-gray-500">
                                        Discounted Price
                                      </span>
                                      <div className="flex items-baseline">
                                        <span className="text-xl font-bold text-green-600">
                                          ₵{parseFloat(discountedPrice).toFixed(2)}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                )}
                                {(calculatedDiscount && calculatedDiscount > 0) && (
                                  <div className="flex-1 p-3 bg-pink-50 rounded-lg">
                                    <div className="flex flex-col">
                                      <span className="text-xs text-pink-500">
                                        Discount
                                      </span>
                                      <span className="text-xl font-bold text-pink-600">
                                        {calculatedDiscount}%
                                      </span>
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          }
                          return null;
                        })()}

                        {/* Bundle Offer Information */}
                        {(() => {
                          // Helper function to check if Bundle has valid data
                          const hasBundleData = (bundle) => {
                            if (!bundle) return false;
                            // Handle both array and object formats
                            const bundleData = Array.isArray(bundle) ? bundle[0] : bundle;
                            return bundleData && (
                              bundleData.original_sum && 
                              bundleData.bundle_price && 
                              bundleData.bundle_savings
                            );
                          };

                          return hasBundleData(deal.bundle);
                        })() && (
                            <div className="p-3 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-100">
                              <h4 className="mb-2 text-sm font-medium text-purple-700">
                                Bundle Offer Details
                              </h4>
                              {(() => {
                                // Handle both array and object formats
                                const bundleData = Array.isArray(deal.bundle) ? deal.bundle : [deal.bundle];
                                return bundleData.map((bundleItem, index) => (
                                  <div key={index} className="space-y-2">
                                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                                      <div className="p-2 bg-white rounded border">
                                        <span className="text-xs text-gray-500">
                                          Original Sum
                                        </span>
                                        <div className="text-lg font-bold text-gray-900">
                                          ₵
                                          {parseFloat(
                                            bundleItem.original_sum
                                          ).toFixed(2)}
                                        </div>
                                      </div>
                                      <div className="p-2 bg-white rounded border">
                                        <span className="text-xs text-gray-500">
                                          Bundle Price
                                        </span>
                                        <div className="text-lg font-bold text-green-600">
                                          ₵
                                          {parseFloat(
                                            bundleItem.bundle_price
                                          ).toFixed(2)}
                                        </div>
                                      </div>
                                      <div className="p-2 bg-white rounded border">
                                        <span className="text-xs text-gray-500">
                                          Total Savings
                                        </span>
                                        <div className="text-lg font-bold text-pink-600">
                                          ₵
                                          {parseFloat(
                                            bundleItem.bundle_savings
                                          ).toFixed(2)}
                                        </div>
                                      </div>
                                    </div>
                                    {bundleItem.bundle_skus &&
                                      bundleItem.bundle_skus.length > 0 && (
                                        <div className="mt-2">
                                          <span className="text-xs text-gray-500">
                                            Included Items:
                                          </span>
                                          <div className="flex flex-wrap gap-1 mt-1">
                                            {bundleItem.bundle_skus.map(
                                              (sku, skuIndex) => (
                                                <span
                                                  key={skuIndex}
                                                  className="px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded"
                                                >
                                                  {sku.product_name} ({sku.sku})
                                                </span>
                                              )
                                            )}
                                          </div>
                                        </div>
                                      )}
                                  </div>
                                ));
                              })()}
                            </div>
                          )}

                        {/* BOGO Offer Information */}
                        {(() => {
                          // Helper function to check if BOGO has valid data
                          const hasBogoData = (bogo) => {
                            if (!bogo) return false;
                            // Handle both array and object formats
                            const bogoData = Array.isArray(bogo) ? bogo[0] : bogo;
                            return bogoData && (
                              bogoData.bogo_item_price || 
                              bogoData.item_price ||
                              (bogoData.bogo_quantity && bogoData.bogo_free_quantity) ||
                              (bogoData.quantity && bogoData.free_quantity)
                            );
                          };

                          return hasBogoData(deal.bogo);
                        })() && (
                            <div className="p-3 bg-gradient-to-r from-blue-50 to-green-50 rounded-lg border border-blue-100">
                              <h4 className="mb-2 text-sm font-medium text-blue-700">
                                Buy One Get One Offer
                              </h4>
                              {(() => {
                                // Handle both array and object formats
                                const bogoData = Array.isArray(deal.bogo) ? deal.bogo : [deal.bogo];
                                return bogoData.map((bogoItem, index) => (
                                  <div key={index} className="space-y-2">
                                    {(bogoItem.bogo_item_price || bogoItem.item_price) && (
                                      <div className="p-2 bg-white rounded border">
                                        <span className="text-xs text-gray-500">
                                          Item Price
                                        </span>
                                        <div className="text-lg font-bold text-blue-600">
                                          ₵
                                          {parseFloat(
                                            bogoItem.bogo_item_price || bogoItem.item_price
                                          ).toFixed(2)}
                                        </div>
                                      </div>
                                    )}
                                    {(bogoItem.bogo_quantity && bogoItem.bogo_free_quantity) ||
                                     (bogoItem.quantity && bogoItem.free_quantity) ? (
                                      <div className="text-sm text-gray-600">
                                        Buy {(bogoItem.bogo_quantity || bogoItem.quantity)} get{" "}
                                        {(bogoItem.bogo_free_quantity || bogoItem.free_quantity)} free
                                      </div>
                                    ) : null}
                                  </div>
                                ));
                              })()}
                            </div>
                          )}

                        {/* Flash Sale Information */}
                        {deal.validity_type === "flash_sale" &&
                          deal.flash &&
                          (() => {
                            // Handle both array and object formats
                            const flashData = Array.isArray(deal.flash) ? deal.flash : [deal.flash];
                            return flashData.some(flash => 
                              flash.flash_start_time && flash.flash_end_time
                            );
                          })() && (
                            <div className="p-3 bg-gradient-to-r from-red-50 to-orange-50 rounded-lg border border-red-100">
                              <h4 className="mb-2 text-sm font-medium text-red-700">
                                Flash Sale Details
                              </h4>
                              {(() => {
                                // Handle both array and object formats
                                const flashData = Array.isArray(deal.flash) ? deal.flash : [deal.flash];
                                return flashData.map((flashItem, index) => (
                                  <div key={index} className="space-y-2">
                                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                                      <div className="p-2 bg-white rounded border">
                                        <span className="text-xs text-gray-500">
                                          Start Time
                                        </span>
                                        <div className="text-sm font-medium text-gray-900">
                                          {formatDate(flashItem.flash_start_time)}
                                        </div>
                                      </div>
                                      <div className="p-2 bg-white rounded border">
                                        <span className="text-xs text-gray-500">
                                          End Time
                                        </span>
                                        <div className="text-sm font-medium text-gray-900">
                                          {formatDate(flashItem.flash_end_time)}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ));
                              })()}
                            </div>
                          )}

                        {/* Offer Description */}
                        <div className="bg-gray-50 rounded-lg p-3">
                          <h4 className="text-xs font-medium text-gray-700 mb-1">
                            Offer Description
                          </h4>
                          <p className="text-sm text-gray-600 break-words whitespace-normal">
                            {deal?.details.description ||
                              "Get amazing discounts on all electronic items. Valid on selected products only. Visit our store or website for more details."}
                          </p>
                        </div>

                        {/* Expiry Information */}
                        <div className="bg-red-50 rounded-lg p-3">
                          <div className="flex items-center justify-between">
                            <div className="flex flex-col">
                              <span className="text-xs text-red-500">
                                {deal?.validity_type === "flash_sale" ? "Flash Sale Period" : "Expires On"}
                              </span>
                              <span className="text-sm font-semibold text-red-600">
                                {deal?.validity_type === "flash_sale" && deal?.flash ? (
                                  (() => {
                                    const flashData = Array.isArray(deal.flash) ? deal.flash : [deal.flash];
                                    const firstFlash = flashData[0];
                                    return firstFlash ? `${formatDate(firstFlash.flash_start_time)} - ${formatDate(firstFlash.flash_end_time)}` : formatDate(deal?.expiry_date);
                                  })()
                                ) : (
                                  formatDate(deal?.expiry_date)
                                )}
                              </span>
                            </div>
                            <div className="bg-red-100 px-3 py-1 rounded-md">
                              <span className="text-xs font-medium text-red-600">
                                {deal?.validity_type === "flash_sale" ? "Flash Sale" : "12 days left"}
                              </span>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}

                    {activeTab === "categories" && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="space-y-6"
                      >
                        <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 shadow-sm border border-gray-100">
                          <div className="flex items-center gap-3 mb-6">
                            <div className="h-8 w-1 bg-gradient-to-b from-pink-500 to-purple-600 rounded-full" />
                            <h4 className="text-lg font-semibold text-gray-900">
                              Categories
                            </h4>
                          </div>

                          <div className="flex flex-wrap gap-2 md:gap-3">
                            {/* Replace with actual categories from your deal object */}
                            {deal?.category.map((index) => (
                              <motion.span
                                key={index}
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: index * 0.05 }}
                                className="group relative"
                              >
                                <div
                                  className="absolute inset-0 bg-gradient-to-r from-pink-500 to-purple-600 
                                              rounded-xl opacity-0 group-hover:opacity-100 blur-lg 
                                              transition-opacity duration-300"
                                />
                                <div
                                  className="relative px-4 py-2.5 bg-white border border-gray-200 
                                              text-gray-700 rounded-xl text-sm font-medium
                                              group-hover:border-transparent group-hover:text-white
                                              group-hover:bg-gradient-to-r from-pink-500 to-purple-600 
                                              transition-all duration-300 shadow-sm
                                              hover:shadow-lg hover:shadow-pink-500/10"
                                >
                                  {index}
                                </div>
                              </motion.span>
                            ))}
                          </div>

                          <div className="mt-6 flex items-center gap-2 text-sm text-gray-500">
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                            </svg>
                            <span>
                              All categories related to this deals/offer
                            </span>
                          </div>
                        </div>
                      </motion.div>
                    )}

                    {activeTab === "contact" && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="space-y-4"
                      >
                        {/* Social Media Section */}
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 mb-2">
                            Social Media
                          </h4>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                            <motion.a
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                              href={deal.contact.social_media.instagram ?? "#"}
                              className="flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-purple-500 to-pink-500 
                                       text-white rounded-lg shadow-md shadow-pink-500/10 hover:shadow-pink-500/20 
                                       transition-shadow duration-200"
                            >
                              <Instagram className="w-5 h-5" />
                              <span className="text-sm font-medium">
                                Instagram
                              </span>
                            </motion.a>

                            <motion.a
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                              href={deal.contact.social_media.facebook ?? "#"}
                              className="flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-500 
                                       text-white rounded-lg shadow-md shadow-blue-500/10 hover:shadow-blue-500/20 
                                       transition-shadow duration-200"
                            >
                              <Facebook className="w-5 h-5" />
                              <span className="text-sm font-medium">
                                Facebook
                              </span>
                            </motion.a>

                            <motion.a
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                              href={deal.contact.social_media.twiter ?? "#"}
                              className="flex items-center justify-center gap-2 px-4 py-3 bg-black 
                                       text-white rounded-lg shadow-md hover:bg-neutral-800
                                       transition-all duration-200"
                            >
                              <svg
                                viewBox="0 0 24 24"
                                className="w-5 h-5 fill-current"
                                aria-hidden="true"
                              >
                                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                              </svg>
                              <span className="text-sm font-medium">
                                Twitter
                              </span>
                            </motion.a>
                          </div>
                        </div>

                        {/* Contact Information */}
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 mb-2">
                            Contact Details
                          </h4>
                          <div className="grid grid-cols-1 gap-2">
                            {/* Phone Number */}
                            <motion.div
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: 0.1 }}
                              className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-100 
                                       hover:border-pink-200 hover:bg-pink-50 transition-colors duration-200"
                            >
                              <div className="p-2 bg-pink-100 rounded-md">
                                <Phone className="w-5 h-5 text-pink-600" />
                              </div>
                              <div>
                                <p className="text-xs text-gray-500 mb-0.5">
                                  Phone Number
                                </p>
                                <a
                                  href={"tel:" + deal.contact.phone_number}
                                  className="text-sm font-medium text-gray-900 hover:text-pink-600"
                                >
                                  + {deal.contact.phone_number}
                                </a>
                              </div>
                            </motion.div>

                            {/* Website */}
                            <motion.div
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: 0.2 }}
                              className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-100 
                                       hover:border-pink-200 hover:bg-pink-50 transition-colors duration-200"
                            >
                              <div className="p-2.5 bg-purple-100 rounded-md">
                                <svg
                                  className="w-5 h-5 text-purple-600"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                  />
                                </svg>
                              </div>
                              <div>
                                <p className="text-sm text-gray-500 mb-1">
                                  Website
                                </p>
                                <a
                                  href={deal.contact.website}
                                  className="text-base font-medium text-gray-900 hover:text-purple-600"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {deal.contact.website}
                                </a>
                              </div>
                            </motion.div>
                          </div>
                        </div>

                        {/* Support Hours Notice */}
                        <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-600 rounded-lg">
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                          <span className="text-xs">
                            Support available Monday to Friday, 9AM - 5PM
                          </span>
                        </div>
                      </motion.div>
                    )}

                    {activeTab === "locations" && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="space-y-6"
                      >
                        {/* Target Locations Section */}
                        <div>
                          <div className="flex gap-3 items-center mb-4">
                            <div className="w-1 h-8 bg-gradient-to-b from-blue-500 to-blue-600 rounded-full" />
                            <h4 className="text-lg font-semibold text-gray-900">
                              Target Locations
                            </h4>
                          </div>
                          
                          {(() => {
                            const locationSections = [];
                            
                            // Handle target_branches (array of branch objects)
                            if (deal.target_branches && Array.isArray(deal.target_branches) && deal.target_branches.length > 0) {
                              locationSections.push(
                                <div key="target_branches" className="space-y-3">
                                  <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200 shadow-sm">
                                    <div className="flex items-center gap-2 mb-3">
                                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                      <h5 className="font-semibold text-blue-800">Target Branches</h5>
                                      <span className="px-2 py-1 text-xs font-medium text-blue-700 bg-blue-100 rounded-full">
                                        {deal.target_branches.length} {deal.target_branches.length === 1 ? 'Branch' : 'Branches'}
                                      </span>
                                    </div>
                                    <div className="grid grid-cols-1 gap-3">
                                      {deal.target_branches.map((branch, index) => (
                                        <div key={index} className="p-3 bg-white rounded-lg border border-blue-100 hover:border-blue-200 hover:shadow-sm transition-all duration-200">
                                          <div className="flex items-start gap-3">
                                            <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                                            <div className="flex-1 min-w-0">
                                              <h6 className="font-medium text-gray-900 mb-1">{branch.name || branch.branch_name}</h6>
                                              <div className="flex gap-2 items-center text-sm text-gray-600">
                                                <Split className="w-4 h-4 text-blue-500 flex-shrink-0" />
                                                <span>{branch.location}, {branch.region}</span>
                                              </div>
                                              {branch.phone_number && (
                                                <div className="flex gap-2 items-center text-sm text-gray-600 mt-1">
                                                  <Phone className="w-4 h-4 text-blue-500 flex-shrink-0" />
                                                  <a href={`tel:${branch.phone_number}`} className="hover:text-blue-600">
                                                    {branch.phone_number}
                                                  </a>
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              );
                            }

                            // Handle target_partners (array of partner objects with branches)
                            if (deal.target_partners && Array.isArray(deal.target_partners) && deal.target_partners.length > 0) {
                              locationSections.push(
                                <div key="target_partners" className="space-y-4">
                                  {deal.target_partners.map((partner, partnerIndex) => (
                                    <div key={partnerIndex} className="overflow-hidden bg-white rounded-xl border border-blue-100 shadow-sm">
                                      <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-100">
                                        <div className="flex justify-between items-center">
                                          <div className="flex items-center gap-2">
                                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                            <h5 className="font-semibold text-blue-800">{partner.partner_name}</h5>
                                          </div>
                                          <span className="px-2 py-1 text-xs font-medium text-blue-700 bg-blue-100 rounded-full">
                                            {partner.branches?.length || 0} {partner.branches?.length === 1 ? 'Branch' : 'Branches'}
                                          </span>
                                        </div>
                                      </div>
                                      
                                      {partner.branches && partner.branches.length > 0 && (
                                        <div className="p-4">
                                          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                                            {partner.branches.map((branch, branchIndex) => (
                                              <div key={branchIndex} className="p-3 bg-white rounded-lg border border-gray-100 hover:border-blue-200 hover:bg-blue-50/30 transition-colors duration-200">
                                                <div className="flex items-start gap-2 mb-2">
                                                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-1.5 flex-shrink-0" />
                                                  <div className="flex-1 min-w-0">
                                                    <h6 className="font-medium text-gray-900 truncate">{branch.name}</h6>
                                                  </div>
                                                </div>
                                                
                                                <div className="space-y-1">
                                                  <div className="flex gap-2 items-center text-sm text-gray-600">
                                                    <Split className="w-4 h-4 text-blue-500 flex-shrink-0" />
                                                    <span className="truncate">{branch.region} - {branch.location}</span>
                                                  </div>
                                                  
                                                  {branch.phone_number && (
                                                    <div className="flex gap-2 items-center text-sm text-gray-600">
                                                      <Phone className="w-4 h-4 text-blue-500 flex-shrink-0" />
                                                      <a href={`tel:${branch.phone_number}`} className="hover:text-blue-600 truncate">
                                                        {branch.phone_number}
                                                      </a>
                                                    </div>
                                                  )}
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              );
                            }

                            // Handle partner_location (array of partner objects with branches)
                            let partnerLocationData = deal.partner_location;
                            if (typeof deal.partner_location === 'string') {
                              try {
                                partnerLocationData = JSON.parse(deal.partner_location);
                              } catch (error) {
                                partnerLocationData = null;
                              }
                            }
                            
                            if (partnerLocationData && Array.isArray(partnerLocationData) && partnerLocationData.length > 0) {
                              locationSections.push(
                                <div key="partner_location" className="space-y-4">
                                  {partnerLocationData.map((partner, partnerIndex) => (
                                    <div key={partnerIndex} className="overflow-hidden bg-white rounded-xl border border-purple-100 shadow-sm">
                                      <div className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 border-b border-purple-100">
                                        <div className="flex justify-between items-center">
                                          <div className="flex items-center gap-2">
                                            <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                                            <h5 className="font-semibold text-purple-800">{partner.partner_name}</h5>
                                          </div>
                                          <span className="px-2 py-1 text-xs font-medium text-purple-700 bg-purple-100 rounded-full">
                                            {partner.branches?.length || 0} {partner.branches?.length === 1 ? 'Branch' : 'Branches'}
                                          </span>
                                        </div>
                                      </div>
                                      
                                      {partner.branches && partner.branches.length > 0 && (
                                        <div className="p-4">
                                          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                                            {partner.branches.map((branch, branchIndex) => (
                                              <div key={branchIndex} className="p-3 bg-white rounded-lg border border-gray-100 hover:border-purple-200 hover:bg-purple-50/30 transition-colors duration-200">
                                                <div className="flex items-start gap-2 mb-2">
                                                  <div className="w-2 h-2 bg-purple-500 rounded-full mt-1.5 flex-shrink-0" />
                                                  <div className="flex-1 min-w-0">
                                                    <h6 className="font-medium text-gray-900 truncate">{branch.name}</h6>
                                                  </div>
                                                </div>
                                                
                                                <div className="space-y-1">
                                                  <div className="flex gap-2 items-center text-sm text-gray-600">
                                                    <Split className="w-4 h-4 text-purple-500 flex-shrink-0" />
                                                    <span className="truncate">{branch.region} - {branch.location}</span>
                                                  </div>
                                                  
                                                  {branch.district && (
                                                    <div className="flex gap-2 items-center text-sm text-gray-600">
                                                      <svg className="w-4 h-4 text-purple-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                                      </svg>
                                                      <span className="truncate">{branch.district}</span>
                                                    </div>
                                                  )}
                                                  
                                                  {branch.suburb && (
                                                    <div className="flex gap-2 items-center text-sm text-gray-600">
                                                      <svg className="w-4 h-4 text-purple-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                                      </svg>
                                                      <span className="truncate">{branch.suburb}</span>
                                                    </div>
                                                  )}
                                                  
                                                  {branch.landmark && (
                                                    <div className="flex gap-2 items-center text-sm text-gray-600">
                                                      <svg className="w-4 h-4 text-purple-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                      </svg>
                                                      <span className="truncate">{branch.landmark}</span>
                                                    </div>
                                                  )}
                                                  
                                                  {branch.phone_number && (
                                                    <div className="flex gap-2 items-center text-sm text-gray-600">
                                                      <Phone className="w-4 h-4 text-purple-500 flex-shrink-0" />
                                                      <a href={`tel:${branch.phone_number}`} className="hover:text-purple-600 truncate">
                                                        {branch.phone_number}
                                                      </a>
                                                    </div>
                                                  )}
                                                  
                                                  {branch.gpsAddress && (
                                                    <div className="flex gap-2 items-center text-sm text-gray-600">
                                                      <svg className="w-4 h-4 text-purple-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                                                      </svg>
                                                      <span className="truncate font-mono text-xs">{branch.gpsAddress}</span>
                                                    </div>
                                                  )}
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              );
                            }

                            // Handle region_location (original format)
                            if (deal.region_location && Array.isArray(deal.region_location) && deal.region_location.length > 0) {
                              if (deal.region_location.length === 1 && 
                                  deal.region_location[0].location === "all" && 
                                  deal.region_location[0].region === "all") {
                                locationSections.push(
                                  <div key="region_location_all" className="space-y-3">
                                    <div className="overflow-hidden relative bg-white rounded-xl border border-green-200 group hover:border-green-300 shadow-sm">
                                      <div className="absolute inset-0 bg-gradient-to-r opacity-0 transition-opacity duration-300 from-green-500/5 to-green-500/5 group-hover:opacity-100" />
                                      <div className="p-4">
                                        <div className="flex justify-between items-center mb-3">
                                          <div className="flex gap-2 items-center">
                                            <div className="w-2 h-2 bg-green-500 rounded-full" />
                                            <h5 className="font-medium text-gray-900">All Target Locations</h5>
                                          </div>
                                          <span className="text-xs font-medium text-green-600 bg-green-100 px-2 py-1 rounded-full">Nationwide</span>
                                        </div>
                                        <div className="space-y-2">
                                          <div className="flex gap-2 items-center text-sm text-gray-600">
                                            <Split className="w-4 h-4 text-green-500" />
                                            <span>Available across all regions and cities</span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              } else {
                                locationSections.push(
                                  <div key="region_location" className="space-y-3">
                                    <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200 shadow-sm">
                                      <div className="flex items-center gap-2 mb-3">
                                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                        <h5 className="font-semibold text-green-800">Regional Locations</h5>
                                        <span className="px-2 py-1 text-xs font-medium text-green-700 bg-green-100 rounded-full">
                                          {deal.region_location.length} {deal.region_location.length === 1 ? 'Location' : 'Locations'}
                                        </span>
                                      </div>
                                      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                                        {deal.region_location.map((locale, index) => (
                                          <div key={index} className="p-3 bg-white rounded-lg border border-green-100 hover:border-green-200 hover:shadow-sm transition-all duration-200">
                                            <div className="flex items-start gap-3">
                                              <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0" />
                                              <div className="flex-1 min-w-0">
                                                <h6 className="font-medium text-gray-900 mb-1">{locale.location}</h6>
                                                <div className="flex gap-2 items-center text-sm text-gray-600">
                                                  <Split className="w-4 h-4 text-green-500 flex-shrink-0" />
                                                  <span>{locale.region}</span>
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  </div>
                                );
                              }
                            }

                            // Return all location sections or no data message
                            if (locationSections.length === 0) {
                              return (
                                <div className="flex justify-center items-center p-6 bg-gray-50 rounded-xl">
                                  <div className="text-center">
                                    <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-3">
                                      <Split className="w-6 h-6 text-gray-400" />
                                    </div>
                                    <p className="text-gray-500">No target location information available</p>
                                  </div>
                                </div>
                              );
                            }

                            return <div className="space-y-4">{locationSections}</div>;
                          })()}
                        </div>

                        {/* Redemption Locations Section */}
                        <div>
                          <div className="flex gap-3 items-center mb-4">
                            <div className="w-1 h-8 bg-gradient-to-b from-green-500 to-green-600 rounded-full" />
                            <h4 className="text-lg font-semibold text-gray-900">
                              Redemption Locations
                            </h4>
                          </div>
                          
                          {(() => {
                            const redemptionSections = [];
                            
                            // Handle redemption_branches (array of branch objects)
                            if (deal.redemption_branches && Array.isArray(deal.redemption_branches) && deal.redemption_branches.length > 0) {
                              redemptionSections.push(
                                <div key="redemption_branches" className="space-y-3">
                                  <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200 shadow-sm">
                                    <div className="flex items-center gap-2 mb-3">
                                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                      <h5 className="font-semibold text-green-800">Redemption Branches</h5>
                                      <span className="px-2 py-1 text-xs font-medium text-green-700 bg-green-100 rounded-full">
                                        {deal.redemption_branches.length} {deal.redemption_branches.length === 1 ? 'Branch' : 'Branches'}
                                      </span>
                                    </div>
                                    <div className="grid grid-cols-1 gap-3">
                                      {deal.redemption_branches.map((branch, index) => (
                                        <div key={index} className="p-3 bg-white rounded-lg border border-green-100 hover:border-green-200 hover:shadow-sm transition-all duration-200">
                                          <div className="flex items-start gap-3">
                                            <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0" />
                                            <div className="flex-1 min-w-0">
                                              <h6 className="font-medium text-gray-900 mb-1">{branch.name || branch.branch_name}</h6>
                                              <div className="flex gap-2 items-center text-sm text-gray-600">
                                                <Split className="w-4 h-4 text-green-500 flex-shrink-0" />
                                                <span>{branch.location}, {branch.region}</span>
                                              </div>
                                              {branch.phone_number && (
                                                <div className="flex gap-2 items-center text-sm text-gray-600 mt-1">
                                                  <Phone className="w-4 h-4 text-green-500 flex-shrink-0" />
                                                  <a href={`tel:${branch.phone_number}`} className="hover:text-green-600">
                                                    {branch.phone_number}
                                                  </a>
                                                </div>
                                              )}
                                              {branch.google_url && (
                                                <div className="flex gap-2 items-center text-sm text-gray-600 mt-1">
                                                  <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                                  </svg>
                                                  <a href={branch.google_url} target="_blank" rel="noopener noreferrer" className="hover:text-green-600">
                                                    View on Google Maps
                                                  </a>
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              );
                            }

                            // Handle redemption_partners (array of partner objects with branches)
                            if (deal.redemption_partners && Array.isArray(deal.redemption_partners) && deal.redemption_partners.length > 0) {
                              redemptionSections.push(
                                <div key="redemption_partners" className="space-y-4">
                                  {deal.redemption_partners.map((partner, partnerIndex) => (
                                    <div key={partnerIndex} className="overflow-hidden bg-white rounded-xl border border-green-100 shadow-sm">
                                      <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 border-b border-green-100">
                                        <div className="flex justify-between items-center">
                                          <div className="flex items-center gap-2">
                                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                            <h5 className="font-semibold text-green-800">{partner.partner_name}</h5>
                                          </div>
                                          <span className="px-2 py-1 text-xs font-medium text-green-700 bg-green-100 rounded-full">
                                            {partner.branches?.length || 0} {partner.branches?.length === 1 ? 'Branch' : 'Branches'}
                                          </span>
                                        </div>
                                      </div>
                                      
                                      {partner.branches && partner.branches.length > 0 && (
                                        <div className="p-4">
                                          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                                            {partner.branches.map((branch, branchIndex) => (
                                              <div key={branchIndex} className="p-3 bg-white rounded-lg border border-gray-100 hover:border-green-200 hover:bg-green-50/30 transition-colors duration-200">
                                                <div className="flex items-start gap-2 mb-2">
                                                  <div className="w-2 h-2 bg-green-500 rounded-full mt-1.5 flex-shrink-0" />
                                                  <div className="flex-1 min-w-0">
                                                    <h6 className="font-medium text-gray-900 truncate">{branch.name}</h6>
                                                  </div>
                                                </div>
                                                
                                                <div className="space-y-1">
                                                  <div className="flex gap-2 items-center text-sm text-gray-600">
                                                    <Split className="w-4 h-4 text-green-500 flex-shrink-0" />
                                                    <span className="truncate">{branch.region} - {branch.location}</span>
                                                  </div>
                                                  
                                                  {branch.phone_number && (
                                                    <div className="flex gap-2 items-center text-sm text-gray-600">
                                                      <Phone className="w-4 h-4 text-green-500 flex-shrink-0" />
                                                      <a href={`tel:${branch.phone_number}`} className="hover:text-green-600 truncate">
                                                        {branch.phone_number}
                                                      </a>
                                                    </div>
                                                  )}
                                                  
                                                  {branch.google_url && (
                                                    <div className="flex gap-2 items-center text-sm text-gray-600">
                                                      <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                                      </svg>
                                                      <a href={branch.google_url} target="_blank" rel="noopener noreferrer" className="hover:text-green-600 truncate">
                                                        View on Google Maps
                                                      </a>
                                                    </div>
                                                  )}
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              );
                            }

                            // Handle redemption_region_location (array of region/location objects)
                            if (deal.redemption_region_location && Array.isArray(deal.redemption_region_location) && deal.redemption_region_location.length > 0) {
                              if (deal.redemption_region_location.length === 1 && 
                                  deal.redemption_region_location[0].location === "all" && 
                                  deal.redemption_region_location[0].region === "all") {
                                redemptionSections.push(
                                  <div key="redemption_region_location_all" className="space-y-3">
                                    <div className="overflow-hidden relative bg-white rounded-xl border border-green-200 group hover:border-green-300 shadow-sm">
                                      <div className="absolute inset-0 bg-gradient-to-r opacity-0 transition-opacity duration-300 from-green-500/5 to-green-500/5 group-hover:opacity-100" />
                                      <div className="p-4">
                                        <div className="flex justify-between items-center mb-3">
                                          <div className="flex gap-2 items-center">
                                            <div className="w-2 h-2 bg-green-500 rounded-full" />
                                            <h5 className="font-medium text-gray-900">All Redemption Locations</h5>
                                          </div>
                                          <span className="text-xs font-medium text-green-600 bg-green-100 px-2 py-1 rounded-full">Nationwide</span>
                                        </div>
                                        <div className="space-y-2">
                                          <div className="flex gap-2 items-center text-sm text-gray-600">
                                            <Split className="w-4 h-4 text-green-500" />
                                            <span>Available across all regions and cities</span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              } else {
                                redemptionSections.push(
                                  <div key="redemption_region_location" className="space-y-3">
                                    <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200 shadow-sm">
                                      <div className="flex items-center gap-2 mb-3">
                                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                        <h5 className="font-semibold text-green-800">Regional Redemption Locations</h5>
                                        <span className="px-2 py-1 text-xs font-medium text-green-700 bg-green-100 rounded-full">
                                          {deal.redemption_region_location.length} {deal.redemption_region_location.length === 1 ? 'Location' : 'Locations'}
                                        </span>
                                      </div>
                                      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                                        {deal.redemption_region_location.map((locale, index) => (
                                          <div key={index} className="p-3 bg-white rounded-lg border border-green-100 hover:border-green-200 hover:shadow-sm transition-all duration-200">
                                            <div className="flex items-start gap-3">
                                              <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0" />
                                              <div className="flex-1 min-w-0">
                                                <h6 className="font-medium text-gray-900 mb-1">{locale.location}</h6>
                                                <div className="flex gap-2 items-center text-sm text-gray-600">
                                                  <Split className="w-4 h-4 text-green-500 flex-shrink-0" />
                                                  <span>{locale.region}</span>
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  </div>
                                );
                              }
                            }

                            // Return all redemption sections or no data message
                            if (redemptionSections.length === 0) {
                              return (
                                <div className="flex justify-center items-center p-6 bg-gray-50 rounded-xl">
                                  <div className="text-center">
                                    <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-3">
                                      <Split className="w-6 h-6 text-gray-400" />
                                    </div>
                                    <p className="text-gray-500">No redemption location information available</p>
                                  </div>
                                </div>
                              );
                            }

                            return <div className="space-y-4">{redemptionSections}</div>;
                          })()}
                        </div>
                      </motion.div>
                    )}

                    {activeTab === "terms" && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="space-y-4"
                      >
                        {/* Terms & Conditions */}
                        <motion.div
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.3 }}
                          className="overflow-hidden relative p-6 bg-gradient-to-br from-blue-50 to-white rounded-2xl border border-blue-100 group"
                        >
                          <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-blue-50 rounded-full opacity-50 filter blur-2xl transition-opacity duration-500 group-hover:opacity-75" />
                          <div className="relative">
                            <div className="flex gap-3 items-center mb-4">
                              <div className="p-2 bg-blue-100 rounded-lg">
                                <ScrollText className="w-5 h-5 text-blue-600" />
                              </div>
                              <h3 className="text-lg font-semibold text-gray-900">
                                Terms & Conditions
                              </h3>
                            </div>
                            <div className="space-y-3">
                              {(() => {
                                const terms = deal?.terms;
                                
                                // Check if it's a URL
                                if (terms && (terms.startsWith('http://') || terms.startsWith('https://'))) {
                                  return (
                                    <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-200">
                                      <div className="mb-3">
                                        <svg className="w-12 h-12 text-blue-600 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                        </svg>
                                        <p className="text-sm text-blue-700 font-medium">Terms available online</p>
                                      </div>
                                      <a 
                                        href={terms} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
                                      >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                        </svg>
                                        View Terms Online
                                      </a>
                                    </div>
                                  );
                                }
                                
                                // Check if it's structured text (contains sections with colons OR bullet points OR numbered lists)
                                if (terms && (terms.includes(':') || terms.includes('•') || terms.includes('-') || terms.includes('1.') || terms.includes('TERMS'))) {
                                  return (
                                    <div className="prose prose-sm max-w-none">
                                      <div className="whitespace-pre-wrap text-sm text-gray-600 leading-relaxed font-sans">
                                        {terms.split('\n').map((line, index) => {
                                          // Handle section headers (lines ending with colon or containing TERMS)
                                          if ((line.trim().endsWith(':') && line.trim().length > 0) || 
                                              (line.trim().toUpperCase().includes('TERMS') && line.trim().length > 0)) {
                                            return (
                                              <div key={index} className="mt-4 mb-2">
                                                <h4 className="text-base font-semibold text-gray-800 border-b border-gray-200 pb-1">
                                                  {line.trim()}
                                                </h4>
                                              </div>
                                            );
                                          }
                                          
                                          // Handle bullet points
                                          if (line.trim().startsWith('•') || line.trim().startsWith('-')) {
                                            return (
                                              <div key={index} className="ml-4 mb-1">
                                                <span className="text-gray-600">{line.trim()}</span>
                                              </div>
                                            );
                                          }
                                          
                                          // Handle numbered lists
                                          if (line.trim().match(/^\d+\./)) {
                                            return (
                                              <div key={index} className="ml-4 mb-1">
                                                <span className="text-gray-600">{line.trim()}</span>
                                              </div>
                                            );
                                          }
                                          
                                          // Handle empty lines
                                          if (line.trim() === '') {
                                            return <div key={index} className="h-2" />;
                                          }
                                          
                                          // Regular text
                                          return (
                                            <div key={index} className="mb-2">
                                              <span className="text-gray-600">{line.trim()}</span>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  );
                                }
                                
                                // Regular text or fallback
                                return (
                                  <div className="prose prose-sm max-w-none">
                                    <pre className="whitespace-pre-wrap text-sm text-gray-600 leading-relaxed font-sans">
                                      {terms ||
                                        "By using this offer, you agree to the following terms and conditions. Management reserves the right to modify or cancel this offer at any time."}
                                    </pre>
                                  </div>
                                );
                              })()}

                              {/* Additional Info Notice */}
                              <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.4 }}
                                className="flex gap-2 items-center p-3 mt-4 rounded-lg border border-blue-100 bg-blue-50/50"
                              >
                                <AlertCircle className="w-4 h-4 text-blue-600" />
                                <span className="text-sm text-blue-600">
                                  These terms are subject to change. Last
                                  updated {moment().format("MMMM DD, YYYY")}
                                </span>
                              </motion.div>
                            </div>
                          </div>
                        </motion.div>
                      </motion.div>
                    )}
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Full-Screen Image Gallery Modal */}
      <AnimatePresence>
        {showImageGallery && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/90 z-[2000] backdrop-blur-sm"
              onClick={() => setShowImageGallery(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="fixed inset-0 z-[2001] flex items-center justify-center p-4"
            >
              <div className="relative max-w-4xl max-h-[90vh] w-full">
                {/* Close Button */}
                <button
                  onClick={() => setShowImageGallery(false)}
                  className="absolute top-4 right-4 z-10 p-2 bg-black/50 text-white rounded-full hover:bg-black/70 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>

                {/* Main Image */}
                <div className="relative bg-white rounded-lg overflow-hidden">
                  <img
                    src={getAllImages()[currentImageIndex]}
                    alt={`Gallery ${currentImageIndex + 1}`}
                    className="w-full h-auto max-h-[80vh] object-contain"
                  />
                </div>

                {/* Navigation Arrows */}
                {getAllImages().length > 1 && (
                  <>
                    <button
                      onClick={prevImage}
                      className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-black/50 text-white rounded-full hover:bg-black/70 transition-colors"
                    >
                      <ChevronLeft className="w-6 h-6" />
                    </button>
                    <button
                      onClick={nextImage}
                      className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-black/50 text-white rounded-full hover:bg-black/70 transition-colors"
                    >
                      <ChevronRight className="w-6 h-6" />
                    </button>
                  </>
                )}

                {/* Thumbnail Strip */}
                {getAllImages().length > 1 && (
                  <div className="absolute bottom-16 left-1/2 -translate-x-1/2 flex gap-2 max-w-full overflow-x-auto">
                    {getAllImages().map((image, index) => (
                      <img
                        key={index}
                        src={image}
                        alt={`Thumbnail ${index + 1}`}
                        className={`w-16 h-16 rounded-lg cursor-pointer object-cover flex-shrink-0 border-2 transition-all ${
                          index === currentImageIndex
                            ? "border-white"
                            : "border-transparent hover:border-white/50"
                        }`}
                        onClick={() => setCurrentImageIndex(index)}
                      />
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </AnimatePresence>
  );
};

export default ViewDealModal;
